import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Search, Download, X, Play, Film, Loader2, Clapperboard, CheckCircle2, Inbox, ArrowLeft, Layers, FolderDown, RefreshCw, MoreVertical, ListOrdered, ClipboardList
} from "lucide-react";
import ReorderClipsModal, { useTicker, elapsedLabel } from "@/ReorderClipsModal";
import OrderAssetHub from "@/OrderAssetHub";
import { toast } from "sonner";
import { useAuth } from "@/AuthContext";
import { useAccess } from "@/useAccess";
import "@/SocialManagerPortal.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_scriptbuilder/artifacts/6hlprpgp_Green_Logo.png";

export const SOCIAL_TYPES = [
  { key: "sneak_peek", label: "Sneak Peek", color: "#96C11F" },
  { key: "just_listed", label: "Just Listed", color: "#fde047" },
  { key: "price_reduction", label: "Price Reduction", color: "#fb923c" },
  { key: "recently_sold", label: "Recently Sold", color: "#34d399" },
  { key: "narratour", label: "NarraTour", color: "#38bdf8" },
  { key: "presentour", label: "PresenTour", color: "#f472b6" },
];
export const CLIENT_TYPES = [
  { key: "post_viewing", label: "Post Viewing", color: "#f59e0b" },
  { key: "post_valuation", label: "Post Valuation", color: "#c4b5fd" },
];
export const TYPE_META = Object.fromEntries([...SOCIAL_TYPES, ...CLIENT_TYPES].map(t => [t.key, t]));

export const shortBranch = (name) =>
  name && name.includes(" - ") ? name.split(" - ").slice(1).join(" - ") : (name || "");

export const formatSize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

export const formatAssetDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

function SocialManagerPortal({ onBack }) {
  const [grouped, setGrouped] = useState(false);
  // "Orders" view (Order Asset Hub) — opened straight away when an order was deep-linked from the tools page.
  const [focusJobId] = useState(() => { try { return sessionStorage.getItem("nc-open-asset-order"); } catch { return null; } });
  const [ordersView, setOrdersView] = useState(() => !!focusJobId);
  useEffect(() => { if (focusJobId) { try { sessionStorage.removeItem("nc-open-asset-order"); } catch { /* private mode */ } } }, [focusJobId]);
  const [groupDl, setGroupDl] = useState(null);
  const [menuKey, setMenuKey] = useState(null);
  const [reorder, setReorder] = useState(null);
  // Re-renders in flight — kept in sessionStorage so closing the modal or reloading the page doesn't lose track of them.
  const [pending, setPending] = useState(() => { try { return JSON.parse(sessionStorage.getItem("smp-pending-renders") || "[]"); } catch { return []; } });
  useTicker(pending.length > 0);
  useEffect(() => { sessionStorage.setItem("smp-pending-renders", JSON.stringify(pending)); }, [pending]);
  useEffect(() => {
    if (!pending.length) return undefined;
    const check = async () => {
      for (const p of pending) {
        try {
          const r = await axios.get(`${API}/reels/status/${p.batch_id}`);
          if (r.data.in_library) {
            toast.success(`${p.title} is in your Social Library`);
            setPending((prev) => prev.filter((x) => x.batch_id !== p.batch_id));
            loadLibrary();
          } else if (r.data.status === "failed") {
            toast.error(`${p.title} failed to render${r.data.error ? ` — ${r.data.error}` : ""}`);
            setPending((prev) => prev.filter((x) => x.batch_id !== p.batch_id));
          }
        } catch (e) {
          if (e.response?.status === 404 || e.response?.status === 403) setPending((prev) => prev.filter((x) => x.batch_id !== p.batch_id));
        }
      }
    };
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [pending]); // eslint-disable-line
  const { user, logout } = useAuth();
  const { can } = useAccess();
  const [branches, setBranches] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBranch, setActiveBranch] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [dlProgress, setDlProgress] = useState(null);
  const [isPortrait, setIsPortrait] = useState(false);

  const closeModal = () => {
    setSelected(null);
    setDlProgress(null);
    setIsPortrait(false);
  };

  const openAsset = (a) => {
    setSelected(a);
    setIsPortrait(a.width && a.height ? a.height > a.width : false);
  };

  const loadLibrary = async () => {
    try {
      const [bRes, aRes] = await Promise.all([
        axios.get(`${API}/social-assets/my-branches`),
        axios.get(`${API}/social-assets`),
      ]);
      setBranches(bRes.data.branches || []);
      setAssets(aRes.data.assets || []);
    } catch {
      toast.error("Failed to load your asset library");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!user || !axios.defaults.headers.common["Authorization"]) return;
    loadLibrary();
  }, [user]);

  const clientNames = [...new Set(branches.map((b) => b.client_name).filter(Boolean))];
  const clientName = clientNames.length === 1 ? clientNames[0] : clientNames.length > 1 ? "All brands" : "";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter(a => {
      if (activeBranch !== "all" && a.branch_id !== activeBranch) return false;
      if (typeFilter !== "all" && a.asset_type !== typeFilter) return false;
      if (q && !(`${a.title} ${a.branch_name} ${a.filename}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [assets, activeBranch, typeFilter, search]);

  const branchCounts = useMemo(() => {
    const map = {};
    assets.forEach(a => { map[a.branch_id] = (map[a.branch_id] || 0) + 1; });
    return map;
  }, [assets]);

  const typeCounts = useMemo(() => {
    const map = {};
    assets.forEach(a => {
      if (activeBranch !== "all" && a.branch_id !== activeBranch) return;
      map[a.asset_type] = (map[a.asset_type] || 0) + 1;
    });
    return map;
  }, [assets, activeBranch]);

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  };

  const groups = useMemo(() => {
    const by = new Map();
    filtered.forEach((a) => {
      const key = a.group_key || `title:${(a.title || "").toLowerCase()}`;
      if (!by.has(key)) by.set(key, { key, title: a.title || a.filename, jobId: a.wms_job_id, branch: a.branch_name, branchId: a.branch_id, items: [], latest: a.created_at });
      const g = by.get(key);
      g.items.push(a);
      if (a.created_at > g.latest) g.latest = a.created_at;
    });
    return [...by.values()].sort((x, y) => (y.latest || "").localeCompare(x.latest || ""));
  }, [filtered]);

  const handleGroupDownload = async (g) => {
    setGroupDl(g.key);
    try {
      const params = { group_key: g.key };
      if (activeBranch !== "all") params.branch_id = activeBranch;
      const res = await axios.get(`${API}/social-assets/grouped/download`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${g.jobId ? `WMS-${g.jobId}-` : ""}${(g.title || "assets").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${g.items.length} file${g.items.length === 1 ? "" : "s"} zipped and downloading`);
    } catch (e) {
      toast.error("Couldn't build that ZIP");
    } finally {
      setGroupDl(null);
    }
  };

  const handleDownload = async (asset) => {
    setDlProgress(0);
    try {
      const res = await axios.get(`${API}/social-assets/${asset.id}/download`, {
        responseType: "blob",
        onDownloadProgress: (e) => {
          const total = e.total || asset.size || 0;
          if (total) setDlProgress(Math.min(99, Math.round((e.loaded / total) * 100)));
        },
      });
      const ext = (asset.filename || "video.mp4").split(".").pop();
      const name = `${(asset.title || "video").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_")}.${ext}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDlProgress(100);
      toast.success("Saved to your device");
      setTimeout(() => setDlProgress(null), 1200);
    } catch {
      toast.error("Download failed — please try again");
      setDlProgress(null);
    }
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="smp-root" data-testid="social-manager-portal">
      <div className="smp-glow smp-glow-a" />
      <div className="smp-glow smp-glow-b" />

      <header className="smp-topbar">
        <div className="smp-brand">
          <img src={LOGO_URL} alt="NicheCom" className="smp-logo" />
          <div className="smp-brand-text">
            <span className="smp-brand-title">Social Library</span>
            {clientName && <span className="smp-brand-sub" data-testid="smp-client-name">{clientName}</span>}
          </div>
        </div>
        <div className="smp-topbar-right">
          {onBack && (
            <button className="smp-back" onClick={onBack} data-testid="smp-back-btn"><ArrowLeft size={14} /> Back to Tools</button>
          )}
          <div className="smp-user-chip" data-testid="smp-user-chip">
            <span className="smp-user-initials">
              {(user?.name || "U").split(" ").map(n => n[0]).join("").slice(0, 2)}
            </span>
            <span className="smp-user-name">{user?.name}</span>
          </div>
          <button className="smp-signout" onClick={logout} data-testid="smp-signout-btn">Sign Out</button>
        </div>
      </header>

      <main className="smp-main">
        <section className="smp-hero">
          <h1 className="smp-hero-title" data-testid="smp-greeting">{greeting()}, {firstName}</h1>
          <p className="smp-hero-sub">Your social-ready assets — preview, download, post.</p>
        </section>

        {branches.length > 1 && (
          <div className="smp-branch-tabs" data-testid="smp-branch-tabs">
            <button
              className={`smp-branch-tab ${activeBranch === "all" ? "active" : ""}`}
              onClick={() => setActiveBranch("all")}
              data-testid="smp-branch-tab-all"
            >
              All Branches <span className="smp-tab-count">{assets.length}</span>
            </button>
            {branches.map(b => (
              <button
                key={b.id}
                className={`smp-branch-tab ${activeBranch === b.id ? "active" : ""}`}
                onClick={() => setActiveBranch(b.id)}
                data-testid={`smp-branch-tab-${b.id}`}
              >
                {shortBranch(b.name)}
                <span className="smp-tab-count">{branchCounts[b.id] || 0}</span>
              </button>
            ))}
          </div>
        )}

        <div className="smp-controls">
          <div className="smp-type-pills" data-testid="smp-type-pills">
            <button
              className={`smp-pill ${typeFilter === "all" && !grouped && !ordersView ? "active" : ""}`}
              onClick={() => { setTypeFilter("all"); setGrouped(false); setOrdersView(false); }}
              data-testid="smp-filter-all"
            >
              All
            </button>
            <button
              className={`smp-pill smp-pill-grouped ${grouped ? "active" : ""}`}
              onClick={() => { setTypeFilter("all"); setOrdersView(false); setGrouped((g) => !g); }}
              data-testid="smp-filter-grouped"
              title="One card per order — download the Sneak Peek and every reel together"
            >
              <Layers size={13} /> Grouped
            </button>
            {user?.asset_hub_access && (
              <button
                className={`smp-pill smp-pill-grouped ${ordersView ? "active" : ""}`}
                onClick={() => { setTypeFilter("all"); setGrouped(false); setOrdersView((v) => !v); }}
                data-testid="smp-filter-orders"
                title="Order Asset Hub — every delivered order with its reels, tours and captions"
              >
                <ClipboardList size={13} /> Orders
              </button>
            )}
            {SOCIAL_TYPES.map(t => (
              <button
                key={t.key}
                className={`smp-pill ${typeFilter === t.key ? "active" : ""}`}
                style={typeFilter === t.key ? { "--pill-accent": t.color } : undefined}
                onClick={() => { setTypeFilter(t.key); setGrouped(false); setOrdersView(false); }}
                data-testid={`smp-filter-${t.key}`}
              >
                {t.label}
                {typeCounts[t.key] ? <span className="smp-pill-count">{typeCounts[t.key]}</span> : null}
              </button>
            ))}
            <span className="smp-pill-divider" />
            <span className="smp-pill-group-label">Client videos</span>
            {CLIENT_TYPES.map(t => (
              <button
                key={t.key}
                className={`smp-pill smp-pill-client ${typeFilter === t.key ? "active" : ""}`}
                style={typeFilter === t.key ? { "--pill-accent": t.color } : undefined}
                onClick={() => { setTypeFilter(t.key); setOrdersView(false); }}
                data-testid={`smp-filter-${t.key}`}
              >
                {t.label}
                {typeCounts[t.key] ? <span className="smp-pill-count">{typeCounts[t.key]}</span> : null}
              </button>
            ))}
          </div>
          <div className="smp-search">
            <Search size={16} className="smp-search-icon" />
            <input
              placeholder="Search address, branch…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="smp-search-input"
            />
          </div>
        </div>

        {pending.length > 0 && (
          <div className="smp-pending-bar" data-testid="smp-pending-bar">
            <Loader2 size={15} className="smp-spin" />
            <span>
              {pending.length === 1 ? <><strong>{pending[0].title}</strong> is rendering · {elapsedLabel(pending[0].started_at)}</> : <><strong>{pending.length} Sneak Peeks</strong> are rendering</>}
              <small> — takes about 2 minutes, it'll appear here automatically.</small>
            </span>
          </div>
        )}

        {ordersView && user?.asset_hub_access ? (
          <OrderAssetHub branchId={activeBranch} search={search} onOpenAsset={openAsset} onGroupDownload={(o) => handleGroupDownload({ key: o.group_key, jobId: o.wms_job_id, title: o.title, items: o.assets })} groupDl={groupDl} focusJobId={focusJobId} refreshKey={assets.length} />
        ) : loading ? (
          <div className="smp-grid">
            {[...Array(6)].map((_, i) => <div key={i} className="smp-skeleton" style={{ animationDelay: `${i * 80}ms` }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="smp-empty" data-testid="smp-empty-state">
            <Inbox size={40} />
            <h3>Nothing here yet</h3>
            <p>Your editing team will push new videos through as soon as they're finished.</p>
          </div>
        ) : grouped ? (
          <div className="smp-groups" data-testid="smp-grouped-grid">
            {groups.map((g, i) => (
              <div key={g.key} className="smp-group" style={{ animationDelay: `${Math.min(i, 12) * 45}ms` }} data-testid={`smp-group-${g.key.replace(/[^a-z0-9]/gi, "-")}`}>
                <div className="smp-group-head">
                  <div>
                    <h4>{g.title}</h4>
                    <div className="smp-card-meta">
                      {g.jobId && <><span className="smp-wms-pill">WMS #{g.jobId}</span><span className="smp-meta-dot">•</span></>}
                      <span>{shortBranch(g.branch)}</span>
                      <span className="smp-meta-dot">•</span>
                      <span>{g.items.length} asset{g.items.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <div className="smp-group-actions">
                    <button className={`smp-group-dl ${groupDl === g.key ? "downloading" : ""}`} onClick={() => handleGroupDownload(g)} disabled={groupDl !== null} data-testid={`smp-group-download-${g.key.replace(/[^a-z0-9]/gi, "-")}`}>
                      {groupDl === g.key ? <><Loader2 size={15} className="smp-spin" /> Zipping…</> : <><FolderDown size={15} /> Download all</>}
                    </button>
                    {can("social-hub.reorder") && (
                      <div className="smp-group-menu-wrap">
                        <button className="smp-group-more" onClick={() => setMenuKey(menuKey === g.key ? null : g.key)} aria-label="More" data-testid={`smp-group-more-${g.key.replace(/[^a-z0-9]/gi, "-")}`}><MoreVertical size={16} /></button>
                        {menuKey === g.key && (
                          <div className="smp-group-menu" data-testid="smp-group-menu">
                            <button onClick={() => { setMenuKey(null); setReorder(g); }} disabled={!g.jobId} title={g.jobId ? "Drag the clips into a new order and re-render the Sneak Peek" : "Needs a WMS order behind it"} data-testid="smp-group-menu-reorder">
                              <ListOrdered size={14} /> Reorder clips &amp; re-render
                            </button>
                            <button onClick={() => { setMenuKey(null); handleGroupDownload(g); }} data-testid="smp-group-menu-download"><FolderDown size={14} /> Download all</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="smp-group-items">
                  {g.items.map((a) => {
                    const meta = TYPE_META[a.asset_type] || { label: a.asset_type, color: "#94a3b8" };
                    return (
                      <button key={a.id} className="smp-group-item" onClick={() => openAsset(a)} data-testid={`smp-group-item-${a.id}`}>
                        <span className="smp-group-thumb">{a.thumb_url ? <img src={a.thumb_url} alt="" loading="lazy" /> : <Film size={18} />}</span>
                        <span className="smp-type-badge" style={{ "--badge-color": meta.color }}>{meta.label}</span>
                      </button>
                    );
                  })}
                  {pending.filter((p) => p.group_key === g.key).map((p) => (
                    <div key={p.batch_id} className="smp-group-item smp-group-pending" title={`${p.title} — rendering`} data-testid={`smp-group-pending-${p.batch_id}`}>
                      <Loader2 size={18} className="smp-spin" />
                      <span className="smp-pending-label">Rendering{p.version ? ` v${p.version}` : ""}</span>
                      <span className="smp-pending-time">{elapsedLabel(p.started_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="smp-grid" data-testid="smp-asset-grid">
            {filtered.map((a, i) => {
              const meta = TYPE_META[a.asset_type] || { label: a.asset_type, color: "#94a3b8" };
              return (
                <div
                  key={a.id}
                  className="smp-card"
                  style={{ animationDelay: `${Math.min(i, 12) * 45}ms` }}
                  onClick={() => openAsset(a)}
                  data-testid={`smp-asset-card-${a.id}`}
                >
                  <div className="smp-thumb">
                    {a.thumb_url ? (
                      <img src={a.thumb_url} alt={a.title} loading="lazy" />
                    ) : (
                      <div className="smp-thumb-fallback"><Film size={30} /></div>
                    )}
                    <span className="smp-type-badge" style={{ "--badge-color": meta.color }}>{meta.label}</span>
                    <div className="smp-play-overlay"><Play size={22} fill="currentColor" /></div>
                  </div>
                  <div className="smp-card-body">
                    <h4 className="smp-card-title">{a.title || a.filename}</h4>
                    <div className="smp-card-meta">
                      <span>{shortBranch(a.branch_name)}</span>
                      <span className="smp-meta-dot">•</span>
                      <span>{formatAssetDate(a.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {reorder && (
        <ReorderClipsModal jobId={reorder.jobId} title={reorder.title} onClose={() => setReorder(null)} onStarted={(p) => setPending((prev) => [...prev.filter((x) => x.batch_id !== p.batch_id), p])} />
      )}

      {selected && (
        <div className="smp-modal-overlay" onClick={closeModal} data-testid="smp-preview-modal">
          <div className={`smp-modal ${isPortrait ? "portrait" : ""}`} onClick={(e) => e.stopPropagation()}>
            <button className="smp-modal-close" onClick={closeModal} data-testid="smp-modal-close">
              <X size={18} />
            </button>
            <div className="smp-modal-video">
              <video
                src={selected.url}
                controls
                autoPlay
                playsInline
                onLoadedMetadata={(e) => setIsPortrait(e.target.videoHeight > e.target.videoWidth)}
                data-testid="smp-modal-video"
              />
            </div>
            <div className="smp-modal-info">
              <div className="smp-modal-details">
                <span
                  className="smp-type-badge smp-type-badge-static"
                  style={{ "--badge-color": (TYPE_META[selected.asset_type] || {}).color || "#94a3b8" }}
                >
                  {(TYPE_META[selected.asset_type] || {}).label || selected.asset_type}
                </span>
                <h3>{selected.title || selected.filename}</h3>
                <p>
                  {shortBranch(selected.branch_name)}
                  <span className="smp-meta-dot">•</span>
                  {formatAssetDate(selected.created_at)}
                  {selected.size ? <><span className="smp-meta-dot">•</span>{formatSize(selected.size)}</> : null}
                </p>
              </div>
              {can("social-hub.download") && (
              <button
                className={`smp-download-btn ${dlProgress !== null ? "downloading" : ""}`}
                onClick={() => handleDownload(selected)}
                disabled={dlProgress !== null && dlProgress < 100}
                data-testid="smp-download-btn"
              >
                {dlProgress === null ? (
                  <><Download size={17} /> Save to Device</>
                ) : dlProgress >= 100 ? (
                  <><CheckCircle2 size={17} /> Saved</>
                ) : (
                  <><Loader2 size={17} className="smp-spin" /> {dlProgress}%</>
                )}
                {dlProgress !== null && dlProgress < 100 && (
                  <span className="smp-dl-fill" style={{ width: `${dlProgress}%` }} />
                )}
              </button>
              )}
              <button className="smp-download-btn smp-wms-sync" disabled title="Coming soon — pushes this video into the right place on WMS" data-testid="smp-wms-sync-btn">
                <RefreshCw size={16} /> WMS Sync <span className="smp-soon">soon</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SocialManagerPortal;
